import { lazy, Suspense, useState } from 'react'

import { Space } from '@tribeplatform/gql-client/types'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useRouter } from '@tribeplatform/react-sdk'
import { useDeleteSpace } from '@tribeplatform/react-sdk/hooks'
import { Button, IconButton } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Hotkey } from '@tribeplatform/react-ui-kit/Hotkey'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useQuery } from '../../hooks/useQuery.js'

const SpaceSettingsModal = lazy(() =>
  import('@tribeplatform/react-components/Space').then(m => ({
    default: m.SpaceSettingsModal,
  })),
)

export const NewSpaceBanner = ({ space }: { space: Space }) => {
  const { $t } = useI18n()
  const { id, relativeUrl } = space
  const query = useQuery()
  const { push } = useRouter()
  const { mutateAsync: deleteSpace, isLoading } = useDeleteSpace()
  const [spaceSettingsModal, setSpaceSettingsModal] = useState(null)

  const handleUndo = async () => {
    try {
      await deleteSpace({ id })
      toast({
        title: $t({
          defaultMessage: '{SPACE_CC} deleted',
          id: 'Admin.Space.Deleted',
        }),
        status: 'success',
      })
      push(`/manage/customizer/create-space`)
    } catch (error) {
      toast({
        title: $t({
          defaultMessage: 'Unable to delete {SPACE}',
          id: 'Admin.Space.FailedToDelete',
        }),
        status: 'error',
      })
    }
  }

  if (!query?.new) {
    return null
  }
  return (
    <>
      <Card className="fixed z-10 bottom-0 end-0 m-5">
        <Card.Content className="flex flex-col space-y-3">
          <div className="flex space-s-5">
            <div className="flex-1 flex-col space-y-2">
              <h4 className="text-lg font-medium">
                <T
                  defaultMessage="Welcome to your new {SPACE}! {emoji}"
                  id="NewSpaceBanner.Title"
                  values={{
                    emoji: '🎉',
                  }}
                />
              </h4>
              <p>
                <T
                  defaultMessage="Customize the blocks in your {SPACE} using Customize button or {key} hotkey."
                  id="NewSpaceBanner.Description"
                  values={{
                    key: <Hotkey variant="key">C</Hotkey>,
                  }}
                />
              </p>
            </div>
            <div>
              <IconButton
                onClick={() => push(relativeUrl)}
                variant="tertiaryNeutral"
                icon={<SvgIcon name="close" />}
                rounded
                className="-mt-1 -me-1"
              ></IconButton>
            </div>
          </div>
          <div className="flex space-s-2">
            <Button
              variant="secondaryNeutral"
              leadingIcon={<SvgIcon name="settings" />}
              onClick={() => setSpaceSettingsModal(true)}
            >
              <T defaultMessage="Settings" id="Generics.Settings" />
            </Button>
            <Button
              variant="secondaryNeutral"
              leadingIcon={<SvgIcon name="edit" />}
              onClick={() => push(`/manage/customizer/spaces/${id}/blocks`)}
            >
              <T defaultMessage="Customize" id="Generics.Customize" />
            </Button>
            <Button
              variant="secondaryNeutral"
              leadingIcon={<SvgIcon name="undo" />}
              onClick={handleUndo}
              loading={isLoading}
            >
              <T defaultMessage="Undo" id="Generics.Undo" />
            </Button>
          </div>
        </Card.Content>
      </Card>
      {spaceSettingsModal && (
        <Suspense fallback={<></>}>
          <SpaceSettingsModal
            space={space}
            onClose={() => setSpaceSettingsModal(false)}
            open
          />
        </Suspense>
      )}
    </>
  )
}
